/**
 * notify-me.js  – Haget back-in-stock / coming-soon notification
 * Handles the "Meddela mig" button and email subscription form.
 */

(function () {
  'use strict';

  const NOTIFY_API = 'https://haget-notify-production.up.railway.app/api/subscribe';
  const SIZES = new Set(['S', 'M', 'L', 'XL', 'XXL']);
  const DISABLED_NOTIFY_COLORS = new Set(['stone', 'lavender']);

  function parseVariantTitle(title) {
    if (!title) return null;
    const parts = title.split(' / ').map(part => part.trim()).filter(Boolean);
    if (parts.length !== 2) return null;
    if (SIZES.has(parts[0].toUpperCase())) return { size: parts[0].toUpperCase(), color: parts[1] };
    if (SIZES.has(parts[1].toUpperCase())) return { size: parts[1].toUpperCase(), color: parts[0] };
    return null;
  }

  function getDisabledNotifyColor(variantTitle = '') {
    const parsed = parseVariantTitle(variantTitle);
    const color = (parsed?.color || variantTitle).trim();
    if (!color) return null;
    return DISABLED_NOTIFY_COLORS.has(color.toLowerCase()) ? color : null;
  }

  // ── Init ────────────────────────────────────────────────────────────────────
  function init() {
    document.addEventListener('click', onDocumentClick);

    // Motion theme fires 'variant:change' on document with { detail: { variant } }
    document.addEventListener('variant:change', onVariantChange);

    // Listen for native Shopify section renders
    document.querySelectorAll('[data-notify-wrap]').forEach(setupWrap);
  }

  // ── Click delegation ────────────────────────────────────────────────────────
  function onDocumentClick(e) {
    // "Meddela mig" button → show form
    const notifyBtn = e.target.closest('[data-notify-btn]');
    if (notifyBtn) {
      e.preventDefault();
      const wrap = notifyBtn.closest('[data-notify-wrap]');
      if (wrap) showForm(wrap);
      return;
    }

    // Submit button inside form
    const submitBtn = e.target.closest('[data-notify-submit]');
    if (submitBtn) {
      e.preventDefault();
      const wrap = submitBtn.closest('[data-notify-wrap]');
      if (wrap) submitForm(wrap, submitBtn);
      return;
    }

    // Close / cancel
    const closeBtn = e.target.closest('[data-notify-close]');
    if (closeBtn) {
      const wrap = closeBtn.closest('[data-notify-wrap]');
      if (wrap) hideForm(wrap);
    }
  }

  // ── Show / hide form ────────────────────────────────────────────────────────
  function showForm(wrap) {
    const form = wrap.querySelector('[data-notify-form]');
    const btn  = wrap.querySelector('[data-notify-btn]');
    if (!form) return;

    form.style.display = 'block';
    form.setAttribute('aria-hidden', 'false');
    if (btn) btn.setAttribute('aria-expanded', 'true');

    const input = form.querySelector('input[type="email"]');
    if (input) setTimeout(() => input.focus(), 50);

    clearMessages(wrap);
  }

  function hideForm(wrap) {
    const form = wrap.querySelector('[data-notify-form]');
    const btn  = wrap.querySelector('[data-notify-btn]');
    if (!form) return;
    form.style.display = 'none';
    form.setAttribute('aria-hidden', 'true');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }

  // ── Submit ──────────────────────────────────────────────────────────────────
  async function submitForm(wrap, submitBtn) {
    const form      = wrap.querySelector('[data-notify-form]');
    const emailInput = form.querySelector('input[type="email"]');
    const email = emailInput?.value?.trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError(wrap, 'Ange en giltig e-postadress.');
      emailInput?.focus();
      return;
    }

    // Gather product data from data attributes on the wrap element
    const variantId    = wrap.dataset.variantId    || '';
    const productId    = wrap.dataset.productId    || '';
    const productTitle = wrap.dataset.productTitle || '';
    const variantTitle = wrap.dataset.variantTitle || '';
    const productHandle= wrap.dataset.productHandle|| '';
    const productUrl   = wrap.dataset.productUrl   || window.location.href;
    const productImage = wrap.dataset.productImage || '';
    const type         = wrap.dataset.type         || 'sold_out';

    setLoading(submitBtn, true);
    clearMessages(wrap);

    try {
      const resp = await fetch(NOTIFY_API, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, variant_id: variantId, product_id: productId,
          product_title: productTitle, variant_title: variantTitle,
          product_handle: productHandle, product_url: productUrl,
          product_image: productImage, type,
          locale: wrap.querySelector('[data-notify-form]')?.dataset.locale || 'sv',
        }),
      });

      const data = await resp.json();

      if (resp.ok && data.success) {
        const form   = wrap.querySelector('[data-notify-form]');
        const locale = form?.dataset.locale || 'sv';
        const vTitle = (wrap.dataset.variantTitle || '').replace('Default Title', '').trim();
        const suffix = vTitle ? ` (${vTitle})` : '';
        const msg = locale === 'en'
          ? `You're on the list${suffix}! We'll email you when it's available.`
          : `Du är nu anmäld${suffix}! Vi hör av oss när produkten finns i lager.`;
        showSuccess(wrap, msg);
        emailInput.value = '';
      } else {
        const form   = wrap.querySelector('[data-notify-form]');
        const locale = form?.dataset.locale || 'sv';
        showError(wrap, data.error || (locale === 'en' ? 'Something went wrong. Please try again.' : 'Något gick fel. Försök igen.'));
      }
    } catch {
      const form   = wrap.querySelector('[data-notify-form]');
      const locale = form?.dataset.locale || 'sv';
      showError(wrap, locale === 'en' ? 'Could not connect. Check your connection.' : 'Kunde inte ansluta. Kontrollera din anslutning.');
    } finally {
      setLoading(submitBtn, false);
    }
  }

  // ── Variant change ──────────────────────────────────────────────────────────
  function onVariantChange(e) {
    const variant = e.detail?.variant;
    if (!variant) return;

    document.querySelectorAll('[data-notify-wrap]').forEach(wrap => {
      // Update variant-specific data attributes
      wrap.dataset.variantId    = variant.id;
      wrap.dataset.variantTitle = variant.title;

      // Update product image to variant image if available
      if (variant.featured_image?.src) {
        wrap.dataset.productImage = variant.featured_image.src;
      }

      // Decide type: coming_soon vs sold_out
      // Mirror the logic in theme.liquid JS for haget-swim-trunks / haget-swim-shorts
      const handle = wrap.dataset.productHandle || '';
      let type = 'sold_out';
      if (
        handle === 'haget-swim-trunks' ||
        (handle === 'haget-swim-shorts' && variant.option1 === 'Coral')
      ) {
        type = 'coming_soon';
      }
      wrap.dataset.type = type;

      // Decide visibility: show if variant is not available AND not pre-orderable
      const disabledColor = getDisabledNotifyColor(variant.title);
      const available  = variant.available;
      const isContinue = variant.inventory_policy === 'continue' && available;
      const shouldShow = !available && !isContinue && !disabledColor;

      // The outer container wraps both Slutsåld btn + Meddela mig
      const notifyOuter = wrap.closest('[data-notify-outer]');
      if (notifyOuter) {
        notifyOuter.style.display = shouldShow ? '' : 'none';
      }

      if (!shouldShow) hideForm(wrap);
    });
  }

  // ── Message helpers ─────────────────────────────────────────────────────────
  function showSuccess(wrap, msg) {
    const el = wrap.querySelector('[data-notify-success]');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }

  function showError(wrap, msg) {
    const el = wrap.querySelector('[data-notify-error]');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }

  function clearMessages(wrap) {
    wrap.querySelectorAll('[data-notify-success],[data-notify-error]')
        .forEach(el => { el.style.display = 'none'; el.textContent = ''; });
  }

  function setLoading(btn, loading) {
    btn.disabled = loading;
    const label = btn.querySelector('[data-notify-btn-label]');
    const spinner = btn.querySelector('[data-notify-spinner]');
    if (label)   label.style.display  = loading ? 'none' : '';
    if (spinner) spinner.style.display = loading ? '' : 'none';
  }

  function setupWrap(wrap) {
    const form = wrap.querySelector('[data-notify-form]');
    if (form) {
      form.style.display = 'none';
      form.setAttribute('aria-hidden', 'true');
    }
  }

  // ── Start ───────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
